import { RootState } from '../store';
import {
    allergySelectors,
    patientHistorySelectors,
    patientPortalDataSelectors,
    patientRxSelectors
} from './selector';
import {
    PatientPortalDataAction,
    PatientPortalDataType
} from '../models/patient-portal-data';

const patientHistorySet = new Set([
    PatientPortalDataType.DrinkingHistory,
    PatientPortalDataType.FamilyHistory,
    PatientPortalDataType.PastMedicalHistory,
    PatientPortalDataType.SocialHistory,
    PatientPortalDataType.SmokingHistory
]);

const specialTypeSet = new Set([
    PatientPortalDataType.DrugAbuseHistory,
    PatientPortalDataType.FallHistory
]);

interface MergeDetail<T> {
    action: PatientPortalDataAction;
    model?: Undefinable<T>;
}

const getMergeDetail = <T>(
    state: RootState,
    type: Undefinable<PatientPortalDataType>,
    id: Undefinable<DictionaryKey>
): Undefinable<MergeDetail<T>> => {
    const patientPortalData = patientPortalDataSelectors.getPatientPortalData(state.patientPortalDatas, type, id);
    if (patientPortalData) {
        return {
            action: patientPortalData.action,
            ...patientPortalData.action !== PatientPortalDataAction.Deleted && { model: patientPortalData.dataModel as T }
        };
    } else {
        const action = id ? PatientPortalDataAction.Updated : PatientPortalDataAction.Created;
        if (type === PatientPortalDataType.Allergy) {
            return {
                action: action,
                model: allergySelectors.getAllergy(state.allergies, id) as Undefinable<T>
            };
        } else if (type === PatientPortalDataType.Medication) {
            return {
                action: action,
                model: patientRxSelectors.getPatientRxByMed(state.patientRxes, id) as Undefinable<T>
            };
        } else if (type && patientHistorySet.has(type)) {
            return {
                action: action,
                model: patientHistorySelectors.getPatientHistory(state.patientHistories, id) as Undefinable<T>
            };
        } else if (type && specialTypeSet.has(type)) {
            return {
                action: action,
                model: patientHistorySelectors.getPatientHistory(state.patientHistories, String(id || '').split('---')[1]) as Undefinable<T>
            };
        }
    }
};

const getMergeModel = <T>(
    state: RootState,
    type: Undefinable<PatientPortalDataType>,
    id: Undefinable<DictionaryKey>
): Undefinable<T> => {
    const detail = getMergeDetail<T>(state, type, id);
    return detail && detail.model;
};

const getMergeField = <T, K>(
    state: RootState,
    type: Undefinable<PatientPortalDataType>,
    id: Undefinable<DictionaryKey>,
    field: keyof T
): Undefinable<K> => {
    const model = getMergeModel<T>(state, type, id);
    return model && model[String(field)];
};

export const selectors = {
    getMergeDetail: getMergeDetail,
    getMergeField: getMergeField,
    getMergeModel: getMergeModel
};
